import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  Product2, Company, RoomType, ThemeLineItemType, ScopeVariant, SituViewWebApiClient, CreateRequestedSupplierStateRequest,
  CreateRequestedSupplierResponse, ScoredThemeReplacementTag,
  RequestedProduct, ThemeReplacementTag
} from 'situview-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config.service';
import { CompanyViewService, ProductService, ProductTypeWithColor, SearchThemeReplacementTagsOptions, ThemeService } from 'src/app/services';
import { Observable, Subscription, Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith, debounceTime } from 'rxjs/operators';

export class ProductSelectorDialogData {
  templateNameFilter: string | undefined;
  companyId: string | undefined;
  groupKey: string | undefined;
  propertyName: string | undefined;
  relatedTagsOrder: string[] | undefined;
  tagGroupsFilter: string[] | undefined;
  tagSelectionEnabled: boolean | undefined; // Allow the user to select tags after selecting a product
  multiSelect: boolean | undefined;
  allowRequestedProducts: boolean = false;
  previousId: string | undefined;
  showRecents: boolean = false;
  recentsVisible: boolean = false;
}

export interface ProductSelectorResultData {
  products: Product2[];
  type: ThemeLineItemType | undefined;
  tags: string[];
  scope: RoomType | undefined;
  scopeVariant: ScopeVariant | undefined;
  groupKey: string | undefined;
  propertyName: string | undefined;
  tagSelectionEnabled: boolean | undefined;
  showRecents: boolean | undefined;
}

@Component({
  selector: 'product-selector-dialog',
  templateUrl: './product-selector-dialog.component.html',
  styleUrls: ['./product-selector-dialog.component.scss']
})
export class ProductSelectorDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  /* Product Drawer */
  isDrawerOpen = false;
  productConfirmed: boolean | undefined;
  selectedType: ThemeLineItemType | undefined;
  selectedScope: RoomType = RoomType.NotSet;
  selectedScopeVariant: ScopeVariant = ScopeVariant.Default;
  url: string | undefined;
  posterUrl: string | undefined;
  selectedTags: string[] = [];

  selectedProducts: (Product2 | RequestedProduct)[] = [];

  /* Search */
  searchTerm = "";

  /* Recents mode */
  private readonly cookieName = 'ProductSelector.Recents';
  private readonly maxRecents = 20;
  showRecents = false;
  recentMode = false;
  recentProducts: Product2[] | undefined;
  filteredRecentProducts: Product2[] | undefined;
  filteringBy = "";
  private readonly SituViewUrl: string;

  /* Request Drawer */
  isRequestDrawerOpen = false;
  requestedProductType: ProductTypeWithColor | undefined;
  requestedCompany: Company | undefined;
  requestingSupplier: boolean = false;
  requestedSupplier: string | undefined;
  requestedSupplierId: string | undefined;

  /* Replacement product */
  showPrevious = false;
  previousUrl = "";

  /* UI Data */
  allProductTypes: ProductTypeWithColor[] = [];
  themeLineItemTypes: ThemeLineItemType[] = [];
  private replacementTags: Promise<ThemeReplacementTag[]> | undefined;
  tags: ScoredThemeReplacementTag[] = [];
  filteredTags: string[] = [];
  remainingFilteredTags: string[] = [];
  showNonRecomendedTags: boolean = false;
  loadingTags: boolean = false;
  roomTypes = RoomType;
  productWidth: number = 150;

  @ViewChild('modelContainer') modelContainer: ElementRef | undefined;
  _subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductSelectorDialogData,
    private dialogRef: MatDialogRef<ProductSelectorDialogData>,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private companyViewService: CompanyViewService,
    private productService: ProductService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    private situViewClient: SituViewWebApiClient) {

    this._subscriptions.push(this.filterChange.pipe(debounceTime(300))
      .subscribe(() => {
        this.filterTags();
      }))
    this.SituViewUrl = configService.config.situViewWebApiBaseUrl;
    if (this.data?.showRecents) {
      this.readRecentsCookie();
      if (this.recentProducts && (this.recentProducts.length > 0)) {
        this.filteredRecentProducts = this.recentProducts;
        this.showRecents = true;
        this.recentMode = (this.data?.recentsVisible) ? true : false;
      }
    }
  }

  ngOnInit(): void {
    Promise.all([this.companyControlInit(), this.loadThemeLineItemTypes(), this.loadProductTypes()]);
  }

  ngAfterViewInit() {
    this.resizeModelViewer();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  readRecentsCookie() {
    try {
      var cookieValue = localStorage.getItem(this.cookieName);
      if (cookieValue) {
        this.recentProducts = JSON.parse(cookieValue) as Product2[];
        if (this.recentProducts && this.recentProducts.length > this.maxRecents) {
          this.recentProducts.length = this.maxRecents;
        }
      }
    }
    catch {
    }
  }

  saveRecentsCookie() {
    try {
      if (this.recentProducts) {
        var cookieValue = JSON.stringify(this.recentProducts);
        localStorage.setItem(this.cookieName, cookieValue);
      }
    }
    catch {
    }
  }

  requestSupplier() {
    if (this.supplierControl.value) {
      let request = new CreateRequestedSupplierStateRequest();
      request.name = this.supplierControl.value;

      this.situViewClient.createRequestedSupplier(request)
        .then((response: CreateRequestedSupplierResponse) => {
          if (response.success) {
            this.requestedSupplier = response.item?.name;
            this.requestedSupplierId = response.item?.id;
          } else {
            this.showMessage(response?.errorMessages![0]);
          }
        })
        .catch((err: any) => {
          this.showMessage(err);
        });
    }
  }

  getProductTypeColor(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.color;
  }

  getProductTypeName(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.name;
  }

  getCompanyLogo(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.logoUrl;
  }

  getCompanyName(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.name;
  }

  onProductDetailsClose() {
    this.productConfirmed = undefined;
    this.selectedType = undefined;
    this.selectedScope = RoomType.NotSet;
    this.selectedScopeVariant = ScopeVariant.Default;
    this.isDrawerOpen = false;
    this.searchTerm = "";
    this.selectedTags = [];
    this.tags = [];
    this.filterTags();
  }

  onRequestClose() {
    this.requestedProductType = undefined;
    this.requestedCompany = undefined;
    this.isRequestDrawerOpen = false;
    this.requestingSupplier = false;
    this.requestedSupplier = undefined;
    this.requestedSupplierId = undefined;
    this.companyControl.reset();
  }

  onRecentSelected(product: Product2) {
    var arr = [product];
    this.onProductSelected(arr);
  }

  onProductSelected(products: Product2[]) {
    this.selectedProducts = products;

    if (this.data.previousId) {
      this.previousUrl = `${this.configService.config.situViewWebApiBaseUrl}/home/getimage?artifactid=${this.data.previousId}&filename=preview.webp`;
      this.showPrevious = true;
    }

    this.isDrawerOpen = true;

    var product = this.selectedProducts[0];
    if (!(product instanceof RequestedProduct) && product?.previewArtifactId) {
      this.url = `${this.configService.config.situViewWebApiBaseUrl}/home/getglb?artifactid=${product.previewArtifactId}&filename=product-web.glb`;
      this.posterUrl = `${this.configService.config.situViewWebApiBaseUrl}/home/getimage?artifactid=${product.previewArtifactId}&filename=preview.webp`;
    } else {
      this.url = undefined;
      this.posterUrl = undefined;
    }
  }

  async selectProduct() {
    if (this.selectedProducts.length > 0) {
      let productType = this.allProductTypes.find(x => x.id === this.selectedProducts[0]?.productTypeId);
      if (productType) {
        this.selectedType = await this.getThemeLineItemType(productType);
      }

      // Resolve the type before confirming so the Select Type step doesn't flash
      this.productConfirmed = true;

      // Skip the tag selection if it has not been asked for
      if (!this.data.tagSelectionEnabled){
        this.dialogRef.close(this.getReturnData());
        return;
      }

      if (this.selectedType) {
        this.loadTagOptions(this.selectedType)
          .then((tags: ScoredThemeReplacementTag[]) => {
              this.tags = tags ?? [];
              if (this.tags?.length === 1) {
                this.selectTag(this.tags[0].tag!);
                this.dialogRef.close(this.getReturnData());
              } else {
                this.filterTags();
              }
          });
      }

    } else {
      this.showMessage('No product selected');
    }
  }

  selectType(themeLineItemType: ThemeLineItemType) {
    if (themeLineItemType) {
      this.selectedType = themeLineItemType;
      this.loadTagOptions(this.selectedType)
        .then((tags: ScoredThemeReplacementTag[]) => {
            this.tags = tags ?? [];
            if (this.tags?.length === 1) {
              this.selectTag(this.tags[0].tag!);
              this.dialogRef.close(this.getReturnData());
            } else {
              this.filterTags();
            }
        });
    }
  }

  loadTagOptions(themeLineItemType?: ThemeLineItemType): Promise<ScoredThemeReplacementTag[]> {
    if (this.data?.companyId) {

      var product = this.selectedProducts[0];

      var options = new SearchThemeReplacementTagsOptions();
      options.themeLineItemTypeId = themeLineItemType?.id;
      options.tagGroupFilter = themeLineItemType?.themeReplacementTagGroup ? [themeLineItemType.themeReplacementTagGroup] : undefined;
      options.productTypeIds = product?.productTypeId ? [product.productTypeId] : undefined;

      if (!(product instanceof RequestedProduct)) {
        options.productSpecificationIds = product?.productSpecificationIds;
      }

      this.loadingTags = true;
      return this.themeService.SearchThemeReplacementTags(this.data.companyId, options)
        .catch((err: any) => {
          return [];
        })
        .finally(() => {
          this.loadingTags = false;
        });
    } else {
      return Promise.resolve([]);
    }
  }

  private filterChange = new Subject();
  emitChange(): void {
    this.filterChange.next();
  }

  private filterTags() {
    var hasHighScore = this.tags.some(x => x.score! > 1);
    var filtered = this.tags
      .filter(x => !this.selectedTags.includes(x.tag!));

    if (this.searchTerm) {
      var filterValue = this.searchTerm.toLowerCase();
      filtered = filtered
        .filter(x => x.tag!.toLowerCase().includes(filterValue));
    }

    var highScore: string[] = [];
    var lowScore: string[] = [];

    filtered.forEach(x => {
      if (x.score! > 1) {
        highScore.push(x.tag!);
      } else {
        lowScore.push(x.tag!);
      }
    });

    if (hasHighScore) {
      this.filteredTags = highScore;
      this.remainingFilteredTags = lowScore;
    } else {
      this.filteredTags = lowScore;
      this.remainingFilteredTags = [];
    }
  }

  selectTag(tag: string) {
    this.selectedTags.push(tag);
    this.filterTags();
  }

  removeTag(tag: string) {
    this.selectedTags = this.selectedTags.filter(x => x.toLowerCase() !== tag.toLowerCase());
    this.filterTags();
  }

  confirmTags() {
    this.dialogRef.close(this.getReturnData());
  }

  selectScope(scope: RoomType) {
    if (scope) {
      this.selectedScope = scope;
      this.dialogRef.close(this.getReturnData());
    }
  }

  getEnumKeyForDisplay(key: string) {
    if (key == null) {
      return '';
    }

    let display = key.replace(/([A-Z])/g, ' $1').trim();
    let words = display.split(' ');
    display = '';
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const nextWord = words[i + 1];
      display += word;
      if (word.length > 1 || nextWord?.length > 1) {
        display += ' ';
      }
    }

    display = display.trim();
    return display;
  }

  /*  Company AutoComplete Filter */
  companyControl = new FormControl();
  supplierControl = new FormControl();
  filteredCompanies: Observable<Company[]> | undefined;
  allCompanies: Company[] = [];
  onCompanySelected(event: any): void {
    this.requestedCompany = event.option.value;
  }

  clearCompany() {
    this.companyControl.reset();
    this.requestedCompany = undefined;
    this.requestedSupplier = undefined;
    this.requestedSupplierId = undefined
  }

  private companyControlInit() {
    this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
        this.filteredCompanies = this.companyControl.valueChanges.pipe(
          startWith(''),
          map(value => typeof value === 'string' ? this._filterCompanies(value) : this.allCompanies)
        );
      });
  }

  private _filterCompanies(value: string): Company[] {
    if (!value) return this.allCompanies;
    const filterValue = value.toLowerCase() ?? "";
    return this.allCompanies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(filterValue));
  }
  /*  Company AutoComplete Filter */

  private filterRecentsByCompany(productCompanyId: string) {
    if (this.recentProducts) {
      this.filteredRecentProducts = this.recentProducts.filter(x => x.companyId === productCompanyId);
      if (this.filteringBy.indexOf("Company") < 0) {
        this.filteringBy = (this.filteringBy != "") ? this.filteringBy + ", Company" : "Company";
      }
    }
  }

  private filterRecentsByProductType(productTypeId: string) {
    if (this.recentProducts) {
      this.filteredRecentProducts = this.recentProducts.filter(x => x.productTypeId === productTypeId);
      if (this.filteringBy.indexOf("Product Type") < 0) {
        this.filteringBy = (this.filteringBy != "") ? this.filteringBy + ", Product Type" : "Product Type";
      }
    }
  }

  private clearRecentsFilters() {
    this.filteredRecentProducts = this.recentProducts;
    this.filteringBy = "";
  }

  private async getThemeLineItemType(productType: ProductTypeWithColor): Promise<ThemeLineItemType | undefined> {
    const matchingTypes = this.themeLineItemTypes.filter(item =>
      item.properties?.some(property => property.templateName?.toLowerCase() === productType.templateName?.toLowerCase())
    );

    if (matchingTypes.length <= 1) {
      return matchingTypes[0];
    }

    const typeByName = matchingTypes.find(item => item.name?.toLowerCase() === productType.name?.toLowerCase());
    if (typeByName) {
      return typeByName;
    }

    // Fall back to the line item types linked to this product type through its replacement tags
    const replacementTags = await this.loadReplacementTags();
    const linkedTypeIds = new Set<string>();
    for (const tag of replacementTags.filter(tag => tag.productTypeIds?.includes(productType.id!))) {
      tag.themeLineItemTypeIds?.forEach(id => linkedTypeIds.add(id));
    }

    const linkedTypes = matchingTypes.filter(item => linkedTypeIds.has(item.id!));
    return linkedTypes.length === 1 ? linkedTypes[0] : undefined;
  }

  private loadReplacementTags(): Promise<ThemeReplacementTag[]> {
    this.replacementTags ??= this.productService.LoadThemeReplacementTags(true, true)
      .catch(() => [] as ThemeReplacementTag[]);
    return this.replacementTags;
  }

  private getReturnData(): ProductSelectorResultData {
    if (this.data?.showRecents) {
      let changed = false;
      if (!this.recentProducts) {
        this.recentProducts = [];
      }
      for (let product of this.selectedProducts) {
        if (!(product instanceof RequestedProduct)) {
          let index = this.recentProducts.findIndex(x => x.id === product.id);
          if (index >= 0) {
            this.recentProducts.splice(index, 1);
          }
          this.recentProducts.unshift(product);
          changed = true;
        }
      }
      if (this.recentProducts.length > this.maxRecents) {
        this.recentProducts.length = this.maxRecents;
        changed = true;
      }
      this.clearRecentsFilters();
      if (changed) {
        this.saveRecentsCookie();
      }
    }
    return {
      products: this.selectedProducts,
      type: this.selectedType,
      tags: this.selectedTags,
      scope: this.selectedScope,
      scopeVariant: this.selectedScopeVariant,
      propertyName: this.data?.propertyName,
      groupKey: this.data?.groupKey,
      tagSelectionEnabled: this.data?.tagSelectionEnabled,
      showRecents: (this.data?.showRecents && this.recentMode)
    } as ProductSelectorResultData;
  }

  private loadCompanies() {
    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
      });
  }

  private loadProductTypes() {
    return this.productService.LoadProductTypesWithColor()
      .then((productTypes: ProductTypeWithColor[]) => {
        this.allProductTypes = productTypes;
      });
  }

  private resizeModelViewer() {
    if (this.modelContainer) {
      const parentWidth = this.modelContainer.nativeElement.offsetWidth;

      // Set the model-viewer size
      this.productWidth = parentWidth * 0.9;
      this.cdr.detectChanges(); // Trigger change detection manually
    }
  }

  private loadThemeLineItemTypes() {
    return this.themeService.LoadThemeLineItemTypes()
      .then((themeLineItemTypes: ThemeLineItemType[]) => {
        this.themeLineItemTypes = themeLineItemTypes ?? [];
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}
